function formatShortDate(iso) {
  const [, month, day] = iso.slice(0, 10).split('-');
  return `${day}/${month}`;
}

/**
 * SVG bar/line chart for admin trend series (ported from Dezoom AdminTrendChart).
 */
export default function renderAdminTrendChart(container, { series, metrics, height = 180 }) {
  container.innerHTML = '';
  if (!series?.length || !metrics?.length) return;

  const barMetrics = metrics.filter((m) => m.type !== 'line');
  const lineMetrics = metrics.filter((m) => m.type === 'line');
  const barCount = barMetrics.length || 1;

  const padding = { top: 16, right: 12, bottom: 32, left: 40 };
  const innerW = Math.max(280, series.length * (barCount > 1 ? 22 : 12));
  const width = innerW + padding.left + padding.right;
  const innerH = height - padding.top - padding.bottom;

  const maxVal = Math.max(
    1,
    ...series.flatMap((row) => metrics.map((m) => Number(row[m.key] ?? 0))),
  );

  const yScale = (v) => padding.top + innerH - (v / maxVal) * innerH;
  const slotW = innerW / series.length;
  const barW = Math.max(2, (slotW - 4) / barCount);
  const labelStep = series.length <= 10 ? 1 : series.length <= 31 ? 5 : 10;

  const wrap = document.createElement('div');
  wrap.className = 'admin-chart-wrap';

  const svgParts = [];
  svgParts.push(`<svg class="admin-chart" viewBox="0 0 ${width} ${height}" role="img" aria-hidden="true">`);

  for (const t of [0, 0.25, 0.5, 0.75, 1]) {
    const y = padding.top + innerH * (1 - t);
    const val = Math.round(maxVal * t);
    svgParts.push(`<line x1="${padding.left}" y1="${y}" x2="${width - padding.right}" y2="${y}" class="admin-chart-grid"/>`);
    svgParts.push(`<text x="${padding.left - 6}" y="${y + 4}" class="admin-chart-axis" text-anchor="end">${val}</text>`);
  }

  series.forEach((row, i) => {
    const slotX = padding.left + i * slotW;
    const title = `${row.date}: ${metrics.map((m) => `${m.label} ${row[m.key] ?? 0}`).join(', ')}`;

    barMetrics.forEach((m, bi) => {
      const v = Number(row[m.key] ?? 0);
      const h = (v / maxVal) * innerH;
      const x = slotX + 2 + bi * barW;
      const y = padding.top + innerH - h;
      svgParts.push(
        `<rect x="${x}" y="${y}" width="${Math.max(1, barW - 1)}" height="${Math.max(0, h)}" fill="${m.color}" class="admin-chart-bar"><title>${title}</title></rect>`,
      );
    });

    if (i === 0 || i % labelStep === 0 || i === series.length - 1) {
      svgParts.push(
        `<text x="${slotX + slotW / 2}" y="${height - 8}" class="admin-chart-axis" text-anchor="middle">${formatShortDate(row.date)}</text>`,
      );
    }
  });

  lineMetrics.forEach((m) => {
    const points = series.map((row, i) => {
      const x = padding.left + i * slotW + slotW / 2;
      const y = yScale(Number(row[m.key] ?? 0));
      return `${x},${y}`;
    }).join(' ');
    svgParts.push(`<polyline points="${points}" fill="none" stroke="${m.color}" stroke-width="2" class="admin-chart-line"/>`);
  });

  svgParts.push('</svg>');

  const legend = document.createElement('ul');
  legend.className = 'admin-chart-legend';
  metrics.forEach((m) => {
    const li = document.createElement('li');
    const swatch = document.createElement('span');
    swatch.className = `admin-chart-legend-swatch${m.type === 'line' ? ' admin-chart-legend-swatch--line' : ''}`;
    if (m.type === 'line') {
      swatch.style.borderColor = m.color;
    } else {
      swatch.style.background = m.color;
    }
    li.appendChild(swatch);
    li.appendChild(document.createTextNode(m.label));
    legend.appendChild(li);
  });

  wrap.innerHTML = svgParts.join('');
  wrap.appendChild(legend);
  container.appendChild(wrap);
}
